import { forwardRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import { Placement, VirtualElement } from '@popperjs/core';
import { styled } from '@spazious/dollhouse-theme';
import { Flex, FlexProps } from '@spazious/dollhouse-uikit';

export interface ToolActionControlsProps extends FlexProps {
  portal?: Element | DocumentFragment;
}

export type ToolActionControlElement = Element | VirtualElement;

export interface ToolActionControlHookOptions {
  placement?: Placement;
  offset?: [number, number];
}

export const ToolActionControlPopUp = <TElement extends ToolActionControlElement = Element>(
  opts?: ToolActionControlHookOptions
) => {
  const { placement = 'bottom', offset = [0, 8] } = { ...opts };
  const [referenceElement, setReferenceElement] = useState<TElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offset,
        },
        enabled: !!offset,
      },
    ],
  });

  const props = { ref: setPopperElement, style: styles.popper, ...attributes.popper };

  return [setReferenceElement as React.RefCallback<TElement>, props] as const;
};

export const ToolActionControls = styled(
  forwardRef<HTMLDivElement, ToolActionControlsProps>(
    ({ portal = document.body as Element, children, ...restProps }, fowardedRef) => {
      return createPortal(
        <Flex ref={fowardedRef} {...restProps}>
          {children}
        </Flex>,
        portal
      );
    }
  )
).withConfig({
  displayName: 'ToolActionControlsProps',
})<ToolActionControlsProps>``;
